import { gql, useApolloClient } from '@apollo/client';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSocketFeed } from '../../../store';
import { myEventsCutoff } from '../my-events-cutoff';
import { useEventsStore } from '../store';

/*
 * Lean stats for the "My Events" header: live count, upcoming count and the next event
 * about to go live — all for the currently selected validator (verifier_email). Fetched
 * separately from the paginated list so counts reflect the whole set, not just one page.
 */

export interface NextEvent {
  eid: string;
  title: string;
  cid: string;
  organizationName?: string;
  start: DateTime;
}

export interface MyEventsStats {
  liveCount: number;
  upcomingCount: number;
  nextEvent?: NextEvent;
  /** All upcoming events (sorted by start), for the upcoming tooltip. */
  upcomingEvents: NextEvent[];
}

const STATS_EVENTS = gql`
  query MyEventsStats($input: GetEventsInput!) {
    getEvents(input: $input) {
      result {
        eid
        name
        startTime
        isLive
        has_asset
        location_secondary_id
        client {
          cid
          organizationName
        }
      }
    }
  }
`;

interface RawEvent {
  eid: string;
  name?: string | null;
  startTime?: string | null;
  isLive?: boolean | null;
  has_asset?: boolean | null;
  location_secondary_id?: number | null;
  client?: {
    cid?: string | null;
    organizationName?: string | null;
  } | null;
}

interface QueryResult {
  getEvents?: {
    result: (RawEvent | null)[];
  } | null;
}

interface QueryVars {
  input: {
    verifier_email?: string;
    ids?: number[];
    end_time_gte?: string;
    start_time_lte?: string;
    page: number;
    limit: number;
  };
}

const STATS_LIMIT = 200;

/*
 * How often the upcoming split is recomputed. No request: it only moves events whose start time has
 * passed out of "upcoming", which the rows alone cannot tell once they stop changing.
 */
const CLOCK_MS = 30000;


export const useMyEventsStats = (verifierEmail?: string, includeFavorites = false): MyEventsStats => {
  const client = useApolloClient();
  const favoriteEvents = useEventsStore((v) => v.favoriteEvents);
  const [now, setNow] = useState(() => DateTime.now());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(DateTime.now());
    }, CLOCK_MS);

    return (): void => {
      clearInterval(timer);
    };
  }, []);

  const favoriteIds = useMemo(
    () => (includeFavorites ? [...favoriteEvents].map(Number).sort((a, b) => a - b) : []),
    [includeFavorites, favoriteEvents]
  );

  /* The time window is computed when the request goes out; frozen at mount, a tab left open went stale. */
  const fetchMine = useCallback(async (): Promise<(RawEvent | null)[]> => {
    const { data } = await client.query<QueryResult, QueryVars>({
      query: STATS_EVENTS,
      variables: {
        input: {
          verifier_email: verifierEmail,
          end_time_gte: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
          start_time_lte: myEventsCutoff().toISO(),
          page: 1,
          limit: STATS_LIMIT
        }
      },
      fetchPolicy: 'no-cache'
    });

    return data.getEvents?.result ?? [];
  }, [client, verifierEmail]);

  const fetchFavorites = useCallback(async (): Promise<(RawEvent | null)[]> => {
    const { data } = await client.query<QueryResult, QueryVars>({
      query: STATS_EVENTS,
      variables: {
        input: { ids: favoriteIds, start_time_lte: myEventsCutoff().toISO(), page: 1, limit: STATS_LIMIT }
      },
      fetchPolicy: 'no-cache'
    });

    return data.getEvents?.result ?? [];
  }, [client, favoriteIds]);

  /* A newly assigned event is not in the current rows, so its id cannot be matched: every batch counts. */
  const mine = useSocketFeed({
    scope: 'my-events-stats:mine',
    cacheKey: verifierEmail ?? '',
    enabled: true,
    fetch: fetchMine
  });

  /* Only a change to a favorite is worth refetching them. */
  const affectsFavorites = useCallback(
    (changed: number[]): boolean => changed.some((id) => favoriteEvents.has(String(id))),
    [favoriteEvents]
  );

  // Favorites by id; skipped entirely when there are none (or when not viewing self).
  const favorites = useSocketFeed({
    scope: 'my-events-stats:favorites',
    cacheKey: favoriteIds.join(','),
    enabled: favoriteIds.length > 0,
    fetch: fetchFavorites,
    affects: affectsFavorites
  });

  return useMemo(() => {
    const seen = new Set<string>();

    // Guard favorites against remembered results: only keep those still favorited.
    const favResults = favorites.rows.filter((e) => e && favoriteEvents.has(e.eid));

    const events = [...mine.rows, ...favResults].filter(
      (e): e is RawEvent => {
        if (!e || !e.eid || !e.has_asset || seen.has(e.eid)) {
          return false;
        }

        seen.add(e.eid);

        return true;
      }
    );

    let liveCount = 0;
    const upcoming: { event: RawEvent; start: DateTime }[] = [];

    for (const event of events) {
      if (event.isLive) {
        liveCount += 1;

        continue;
      }

      const start = DateTime.fromISO(event.startTime ?? '');

      if (start.isValid && start > now) {
        upcoming.push({ event, start });
      }
    }

    upcoming.sort((a, b) => a.start.toMillis() - b.start.toMillis());

    const upcomingEvents: NextEvent[] = upcoming.map(({ event, start }) => ({
      eid: event.eid,
      title: event.name ?? 'Untitled',
      cid: `${event.client?.cid ?? ''}.${event.location_secondary_id ?? ''}`,
      organizationName: event.client?.organizationName ?? undefined,
      start
    }));

    return {
      liveCount,
      upcomingCount: upcomingEvents.length,
      nextEvent: upcomingEvents[0],
      upcomingEvents
    };
  }, [mine.rows, favorites.rows, favoriteEvents, now]);
};
